/**
 * Copyright (c) 2014, Facebook, Inc. All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree. An additional grant
 * of patent rights can be found in the PATENTS file in the same directory.
 */
'use strict';

var crypto = require('crypto');
var colors = require('./colors');
var fs = require('graceful-fs');
var path = require('path');
var stringify = require('json-stable-stringify');

var DEFAULT_CONFIG_VALUES = {
  bail: false,
  cacheDirectory: path.resolve(__dirname, '..', '..', '.haste_cache'),
  coverageCollector: require.resolve('../IstanbulCollector'),
  coverageReporters: [ 'json', 'text', 'lcov', 'clover' ],
  globals: {},
  moduleFileExtensions: ['js', 'json'],
  moduleLoader: require.resolve('../HasteModuleLoader/HasteModuleLoader'),
  preprocessorIgnorePatterns: [],
  modulePathIgnorePatterns: [],
  testDirectoryName: '__tests__',
  testEnvironment: require.resolve('../JSDomEnvironment'),
  testEnvData: {},
  testFileExtensions: ['js'],
  testPathDirs: ['<rootDir>'],
  testPathIgnorePatterns: ['/node_modules/'],
  testReporter: require.resolve('../IstanbulTestReporter'),
  testRunner: require.resolve('../jasmineTestRunner/jasmineTestRunner'),
  testURL: 'about:blank',
  noHighlight: false,
  noStackTrace: false,
  preprocessCachingDisabled: false,
  verbose: false
};

// This shows up in the stack trace when a test file throws an unhandled error
// when evaluated. Node's require prints Object.<anonymous> when initializing
// modules, so do the same here solely for visual consistency.
var EVAL_RESULT_VARIABLE = 'Object.<anonymous>';

function _replaceRootDirTags(rootDir, config) {
  switch (typeof config) {
    case 'object':
      if (config instanceof RegExp) {
        return config;
      }

      if (Array.isArray(config)) {
        return config.map(function(item) {
          return _replaceRootDirTags(rootDir, item);
        });
      }

      if (config !== null) {
        var newConfig = {};
        for (var configKey in config) {
          newConfig[configKey] =
            configKey === 'rootDir'
            ? config[configKey]
            : _replaceRootDirTags(rootDir, config[configKey]);
        }
        return newConfig;
      }
      break;
    case 'string':
      if (!/^<rootDir>/.test(config)) {
        return config;
      }

      return pathNormalize(path.resolve(
        rootDir,
        './' + path.normalize(config.substr('<rootDir>'.length))
      ));
  }
  return config;
}

function escapeStrForRegex(str) {
  return str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

/**
 * Given the coverage info for a single file (as output by
 * CoverageCollector.js), return an array whose entries are bools indicating
 * whether anything on the line could have been covered and was, or null if the
 * line wasn't measurable (like empty lines, declaration keywords, etc).
 *
 * For example, for the following coverage info:
 *
 * COVERED:     var a = [];
 * NO CODE:
 * COVERED:     for (var i = 0; i < a.length; i++)
 * NOT COVERED:   console.log('hai!');
 *
 * You'd get an array that looks like this:
 *
 * [true, null, true, false]
 */
function getLineCoverageFromCoverageInfo(coverageInfo) {
  var coveredLines = {};
  coverageInfo.coveredSpans.forEach(function(coveredSpan) {
    var startLine = coveredSpan.start.line;
    var endLine = coveredSpan.end.line;
    for (var i = startLine - 1; i < endLine; i++) {
      coveredLines[i] = true;
    }
  });

  var uncoveredLines = {};
  coverageInfo.uncoveredSpans.forEach(function(uncoveredSpan) {
    var startLine = uncoveredSpan.start.line;
    var endLine = uncoveredSpan.end.line;
    for (var i = startLine - 1; i < endLine; i++) {
      uncoveredLines[i] = true;
    }
  });

  var sourceLines = coverageInfo.sourceText.trim().split('\n');

  return sourceLines.map(function(line, lineIndex) {
    if (uncoveredLines[lineIndex] === true) {
      return false;
    } else if (coveredLines[lineIndex] === true) {
      return true;
    } else {
      return null;
    }
  });
}

/**
 * Given the coverage info for a single file (as output by
 * CoverageCollector.js), return the decimal percentage of lines in the file
 * that had any coverage info.
 *
 * For example, for the following coverage info:
 *
 * COVERED:     var a = [];
 * NO CODE:
 * COVERED:     for (var i = 0; i < a.length; i++)
 * NOT COVERED:   console.log('hai');
 *
 * You'd get: 2/3 = 0.666666
 */
function getLinePercentCoverageFromCoverageInfo(coverageInfo) {
  var lineCoverage = getLineCoverageFromCoverageInfo(coverageInfo);
  var numMeasuredLines = 0;
  var numCoveredLines = lineCoverage.reduce(function(counter, lineIsCovered) {
    if (lineIsCovered !== null) {
      numMeasuredLines++;
      if (lineIsCovered === true) {
        counter++;
      }
    }
    return counter;
  }, 0);

  return numCoveredLines / numMeasuredLines;
}

function normalizeConfig(config) {
  var newConfig = {};

  // Assert that there *is* a rootDir
  if (!config.hasOwnProperty('rootDir')) {
    throw new Error('No rootDir config value found!');
  }

  config.rootDir = pathNormalize(config.rootDir);

  // Normalize user-supplied config options
  Object.keys(config).reduce(function(newConfig, key) {
    var value;
    switch (key) {
      case 'collectCoverageOnlyFrom':
        value = Object.keys(config[key]).reduce(function(normObj, filePath) {
          filePath = pathNormalize(path.resolve(
            config.rootDir,
            _replaceRootDirTags(config.rootDir, filePath)
          ));
          normObj[filePath] = true;
          return normObj;
        }, {});
        break;

      case 'testPathDirs':
        value = config[key].map(function(scanDir) {
          return pathNormalize(path.resolve(
            config.rootDir,
            _replaceRootDirTags(config.rootDir, scanDir)
          ));
        });
        break;

      case 'cacheDirectory':
      case 'scriptPreprocessor':
      case 'setupEnvScriptFile':
      case 'setupTestFrameworkScriptFile':
        value = pathNormalize(path.resolve(
          config.rootDir,
          _replaceRootDirTags(config.rootDir, config[key])
        ));
        break;

      case 'preprocessorIgnorePatterns':
      case 'testPathIgnorePatterns':
      case 'modulePathIgnorePatterns':
      case 'unmockedModulePathPatterns':
        // _replaceRootDirTags is specifically well-suited for substituting
        // <rootDir> in paths (it deals with properly interpreting relative path
        // separators, etc).
        //
        // For patterns, direct global substitution is far more ideal, so we
        // special case substitutions for patterns here.
        value = config[key].map(function(pattern) {
          return pattern.replace(/<rootDir>/g, config.rootDir);
        });
        break;
      case 'bail':
      case 'preprocessCachingDisabled':
      case 'coverageReporters':
      case 'collectCoverage':
      case 'coverageCollector':
      case 'globals':
      case 'moduleLoader':
      case 'name':
      case 'persistModuleRegistryBetweenSpecs':
      case 'rootDir':
      case 'setupJSLoaderOptions':
      case 'setupJSTestLoaderOptions':
      case 'setupJSMockLoaderOptions':
      case 'testDirectoryName':
      case 'testEnvData':
      case 'testFileExtensions':
      case 'testPathPattern':
      case 'testReporter':
      case 'testURL':
      case 'moduleFileExtensions':
      case 'noHighlight':
      case 'noStackTrace':
      case 'verbose':
        value = config[key];
        break;

      default:
        throw new Error('Unknown config option: ' + key);
    }
    newConfig[key] = value;
    return newConfig;
  }, newConfig);

  // If any config entries weren't specified but have default values, apply the
  // default values
  Object.keys(DEFAULT_CONFIG_VALUES).reduce(function(newConfig, key) {
    if (!newConfig[key]) {
      newConfig[key] = DEFAULT_CONFIG_VALUES[key];
    }
    return newConfig;
  }, newConfig);

  // Fill in some default values for node-haste config
  newConfig.setupJSLoaderOptions = newConfig.setupJSLoaderOptions || {};
  newConfig.setupJSTestLoaderOptions = newConfig.setupJSTestLoaderOptions || {};
  newConfig.setupJSMockLoaderOptions = newConfig.setupJSMockLoaderOptions || {};

  if (!newConfig.setupJSTestLoaderOptions.extensions) {
    newConfig.setupJSTestLoaderOptions.extensions =
      newConfig.testFileExtensions.map(_addDot);
  }

  if (!newConfig.setupJSLoaderOptions.extensions) {
    newConfig.setupJSLoaderOptions.extensions = uniqueStrings(
      newConfig.moduleFileExtensions.map(_addDot).concat(
        newConfig.setupJSTestLoaderOptions.extensions
      )
    );
  }

  if (!newConfig.setupJSMockLoaderOptions.extensions) {
    newConfig.setupJSMockLoaderOptions.extensions =
      newConfig.setupJSLoaderOptions.extensions;
  }

  return _replaceRootDirTags(newConfig.rootDir, newConfig);
}

function _addDot(ext) {
  return '.' + ext;
}

function uniqueStrings(set) {
  var newSet = [];
  var has = {};
  set.forEach(function (item) {
    if (!has[item]) {
      has[item] = true;
      newSet.push(item);
    }
  });
  return newSet;
}

function pathNormalize(dir) {
  return path.normalize(dir.replace(/\\/g, '/')).replace(/\\/g, '/');
}

function readFile(filePath) {
  return new Promise(function(resolve, reject) {
    fs.readFile(filePath, 'utf8', function(err, data) {
      if (err) {
        reject(err);
        return;
      }
      resolve(data);
    });
  });
}

function loadConfigFromFile(filePath) {
  var fileDir = path.dirname(filePath);
  return readFile(filePath).then(function(fileData) {
    var config = JSON.parse(fileData);
    if (!config.hasOwnProperty('rootDir')) {
      config.rootDir = fileDir;
    } else {
      config.rootDir = path.resolve(fileDir, config.rootDir);
    }
    return normalizeConfig(config);
  });
}

function loadConfigFromPackageJson(filePath) {
  var pkgJsonDir = path.dirname(filePath);
  return readFile(filePath).then(function(fileData) {
    var packageJsonData = JSON.parse(fileData);
    var config = packageJsonData.jest;
    config.name = packageJsonData.name;
    if (!config.hasOwnProperty('rootDir')) {
      config.rootDir = pkgJsonDir;
    } else {
      config.rootDir = path.resolve(pkgJsonDir, config.rootDir);
    }
    return normalizeConfig(config);
  });
}

function cleanupCacheFile(cachePath) {
  try {
    fs.unlinkSync(cachePath);
  } catch (e) {
    /*ignore errors*/
  }
}

function storeCacheRecord(mtime, fileData, filePath) {
  _contentCache[filePath] = {mtime: mtime, content: fileData};
  return fileData;
}

// To avoid stringifiying the config multiple times
var configToJsonMap = new Map();

// There are two layers of caching: in memory (always enabled),
// and on disk (enabled by default, and managed by the
// `preprocessCachingDisabled` option). The preprocessor script can also
// provide hashing function for the cache key.
var _contentCache = {};
function readAndPreprocessFileContent(filePath, config) {
  var cacheRec;
  var mtime = fs.statSync(filePath).mtime;
  if (_contentCache.hasOwnProperty(filePath)) {
    cacheRec = _contentCache[filePath];
    if (cacheRec.mtime.getTime() === mtime.getTime()) {
      return cacheRec.content;
    }
  }

  var fileData = fs.readFileSync(filePath, 'utf8');

  // If the file data starts with a shebang remove it (but leave the line empty
  // to keep stack trace line numbers correct)
  if (fileData.substr(0, 2) === '#!') {
    fileData = fileData.replace(/^#!.*/, '');
  }

  if (config.scriptPreprocessor &&
      !config.preprocessorIgnorePatterns.some(function(pattern) {
        return new RegExp(pattern).test(filePath);
      })) {
    try {
      var preprocessor = require(config.scriptPreprocessor);
      if (typeof preprocessor.process !== 'function') {
        throw new TypeError('Preprocessor should export `process` function.');
      }
      // On disk cache is enabled by default, unless explicitly disabled.
      if (config.preprocessCachingDisabled !== true) {
        var cacheDir = path.join(
          config.cacheDirectory,
          'preprocess-cache'
        );

        if (!fs.existsSync(cacheDir)) {
          try {
            fs.mkdirSync(cacheDir);
          } catch(e) {
            if (e.code !== 'EEXIST') {
              throw e;
            }
          }

          fs.chmodSync(cacheDir, '777');
        }

        var cacheKey;
        // If preprocessor defines custom cache hashing and
        // invalidating logic.
        if (typeof preprocessor.getCacheKey === 'function') {
          cacheKey = preprocessor.getCacheKey(
            fileData,
            filePath,
            {}, // options
            [], //excludes
            config
          );
        } else {
          var configStr = configToJsonMap.get(config);
          if (!configStr) {
            configStr = stringify(config);
            configToJsonMap.set(config, configStr);
          }

          // Default cache hashing.
          cacheKey = crypto.createHash('md5')
            .update(fileData)
            .update(configStr)
            .digest('hex');
        }

        var extension = path.extname(filePath);
        var cachePath = path.join(
          cacheDir,
          path.basename(filePath, extension) + '_' + cacheKey + extension
        );

        if (fs.existsSync(cachePath)) {
          try {
            var cachedData = fs.readFileSync(cachePath, 'utf8');
            if (cachedData) {
              return storeCacheRecord(mtime, cachedData, filePath);
            } else {
              // In this case we must have somehow created the file but failed
              // to write to it, lets just delete it and move on
              cleanupCacheFile(cachePath);
            }
          } catch (e) {
            e.message = 'Failed to read preprocess cache file: ' + cachePath;
            cleanupCacheFile(cachePath);
            throw e;
          }
        }

        fileData = preprocessor.process(
          fileData,
          filePath,
          {}, // options
          [], // excludes
          config
        );

        try {
          fs.writeFileSync(cachePath, fileData);
        } catch (e) {
          e.message = 'Failed to cache preprocess results in: ' + cachePath;
          cleanupCacheFile(cachePath);
          throw e;
        }

      } else {
        fileData = preprocessor.process(
          fileData,
          filePath,
          {}, // options
          [], // excludes
          config
        );
      }
    } catch (e) {
      e.message = config.scriptPreprocessor + ': ' + e.message;
      throw e;
    }
  }
  return storeCacheRecord(mtime, fileData, filePath);
}

function runContentWithLocalBindings(environment, scriptContent, scriptPath,
                                     bindings) {
  var boundIdents = Object.keys(bindings);
  try {
    var wrapperScript = 'this["' + EVAL_RESULT_VARIABLE + '"] = ' +
      'function (' + boundIdents.join(',') + ') {' +
      scriptContent +
      '\n};';
    environment.runSourceText(
      wrapperScript,
      scriptPath
    );
  } catch (e) {
    e.message = scriptPath + ': ' + e.message;
    throw e;
  }

  var wrapperFunc = environment.global[EVAL_RESULT_VARIABLE];
  delete environment.global[EVAL_RESULT_VARIABLE];

  var bindingValues = boundIdents.map(function(ident) {
    return bindings[ident];
  });

  try {
    // Node modules are executed with the `exports` as context.
    // If not a node module then this should be undefined.
    wrapperFunc.apply(bindings.exports, bindingValues);
  } catch (e) {
    e.message = scriptPath + ': ' + e.message;
    throw e;
  }
}

/**
 * Given a test result, return a human readable string representing the
 * failures.
 *
 * @param {Object} testResult
 * @param {Object} config Containing the following keys:
 *   `rootPath` - Root directory (for making stack trace paths relative).
 *   `useColor` - True if message should include color flags.
 * @return {String}
 */
function formatFailureMessage(testResult, config) {
  var rootPath = config.rootPath;
  var useColor = config.useColor;

  var colorize = useColor ? colors.colorize : function (str) { return str; };
  var ancestrySeparator = ' \u203A ';
  var descBullet = colorize('\u25cf ', colors.BOLD);
  var msgBullet = '  - ';
  var msgIndent = msgBullet.replace(/./g, ' ');

  if (testResult.testExecError) {
    var text = testResult.testExecError;
    return descBullet + colorize('Runtime Error', colors.BOLD) + '\n' + text;
  }

  return testResult.testResults.filter(function (result) {
    return result.failureMessages.length !== 0;
  }).map(function(result) {
    var failureMessages = result.failureMessages.map(function(errorMsg) {
      errorMsg = errorMsg.split('\n').map(function(line) {
        // Extract the file path from the trace line.
        var matches = line.match(/(^\s+at .*?\()([^()]+)(:[0-9]+:[0-9]+\).*$)/);
        if (!matches) {
          matches = line.match(/(^\s+at )([^()]+)(:[0-9]+:[0-9]+.*$)/);
          if (!matches) {
            return line;
          }
        }
        var filePath = matches[2];
        // Filter out noisy and unhelpful lines from the stack trace.
        if (STACK_TRACE_LINE_IGNORE_RE.test(filePath)) {
          return null;
        }
        return (
          matches[1] +
          path.relative(rootPath, filePath) +
          matches[3]
        );
      }).filter(function(line) {
        return line !== null;
      }).join('\n');

      return msgBullet + errorMsg.replace(/\n/g, '\n' + msgIndent);
    }).join('\n');

    var testTitleAncestry = result.ancestorTitles.map(function(title) {
      return colorize(title, colors.BOLD);
    }).join(ancestrySeparator) + ancestrySeparator;

    return descBullet + testTitleAncestry + result.title + '\n' +
      failureMessages;
  }).join('\n');
}

function formatMsg(msg, color, _config) {
  _config = _config || {};
  if (_config.noHighlight) {
    return msg;
  }
  return colors.colorize(msg, color);
}

function deepCopy(obj) {
  var newObj = {};
  var value;
  for (var key in obj) {
    value = obj[key];
    if (typeof value === 'object' && value !== null) {
      value = deepCopy(value);
    }
    newObj[key] = value;
  }
  return newObj;
}

// A RegExp that matches paths that should not be included in error stack traces
// (mostly because these paths represent noisy/unhelpful libs)
var STACK_TRACE_LINE_IGNORE_RE = new RegExp([
  '^timers.js$',
  '^' + path.resolve(__dirname, '..', 'lib', 'moduleMocker.js'),
  '^' + path.resolve(__dirname, '..', '..', 'vendor', 'jasmine')
].join('|'));

exports.deepCopy = deepCopy;
exports.escapeStrForRegex = escapeStrForRegex;
exports.formatMsg = formatMsg;
exports.getLineCoverageFromCoverageInfo = getLineCoverageFromCoverageInfo;
exports.getLinePercentCoverageFromCoverageInfo =
  getLinePercentCoverageFromCoverageInfo;
exports.loadConfigFromFile = loadConfigFromFile;
exports.loadConfigFromPackageJson = loadConfigFromPackageJson;
exports.normalizeConfig = normalizeConfig;
exports.pathNormalize = pathNormalize;
exports.readAndPreprocessFileContent = readAndPreprocessFileContent;
exports.runContentWithLocalBindings = runContentWithLocalBindings;
exports.formatFailureMessage = formatFailureMessage;



//////////////////
// WEBPACK FOOTER
// ./~/jest-cli/src/lib/utils.js
// module id = 46
// module chunks = 0